import type { CardChoice, Ctx } from '@/core/context';
import type { CartState, FlowState } from '@/core/state';
import type { Flow, FloorIdT } from '@/content/schema';
import { bus } from '@/core/bus';
import { nextRandom } from '@/core/rng';
import { fmtClock } from '@/core/time';
import { bump } from './achievements';
import { gainPoints, gainXp } from './progress';
import { tr } from '@/i18n';

/**
 * v0.8 線邊物流（content/flow.json）
 *
 * 1. 生管開工單 → 產線依工單生產：成品陸續裝進「成品收料車」（烏龜車＋籃子，一車最多 5 籃）。
 *    收料車滿了，作業員推進「成品電梯」、用無線電通知出貨倉儲課 → 1F 倉儲收貨、拉出電梯 → 出貨區裝車。
 *    半成品（烤、炸、炒）也會一籃一籃變多，滿了推去下一站（真空冷卻室）。
 * 2. 食材不能直接放地上：線邊的籃子下面一定有烏龜車（地圖上的籃子都墊了車；放下籃子時線邊要有車）。
 * 3. 每裝一籃成品要用掉一個乾淨的空籃；每做幾籃，線頭就會空出一個髒籃。
 *    空籃從洗籃區的「風乾區」推過來；髒籃推去「洗籃區入口」交給洗籃人員，洗好了會出現在風乾區。
 *    生食・一般用籃在 1F 洗籃區洗；熟食用籃在 2F 熟食洗籃區洗（不能混洗）。
 *
 * 玩家不做的事，同事（NPC）過一陣子會做；生產部的玩家每天會遇到幾次「線邊缺空籃」，要自己去補。
 */

export const EMPTY = 'empty';
export const DIRTY = 'dirty';
const PROD = 'prod:';
const SEMI = 'semi:';
type Day = Ctx['day'];
type Station = Flow['stations'][number];
type BasketKind = 'empty' | 'dirty' | 'prod' | 'semi' | 'food';

// ------------------------------------------------------------------ 籃子內容
export const basketKind = (content: string): BasketKind =>
  content === EMPTY ? 'empty' : content === DIRTY ? 'dirty' : content.startsWith(PROD) ? 'prod' : content.startsWith(SEMI) ? 'semi' : 'food';

export function basketLabel(c: Ctx['c'], content: string): string {
  switch (basketKind(content)) {
    case 'empty': return tr("空籃（乾淨）");
    case 'dirty': return tr("髒籃");
    case 'prod': { const p = c.productById.get(content.slice(PROD.length)); return tr("{0}成品", p?.name ?? ''); }
    case 'semi': { const s = c.semiById.get(content.slice(SEMI.length)); return tr("{0}（半成品）", s?.name ?? tr("半成品")); }
    default: return c.ingredientById.get(content)?.name ?? content;
  }
}
export function basketColor(c: Ctx['c'], content: string): string {
  switch (basketKind(content)) {
    case 'empty': return '#e6f0f4';
    case 'dirty': return '#7d6b4f';
    case 'prod': return c.productById.get(content.slice(PROD.length))?.color ?? '#f2b134';
    case 'semi': return '#d9893a';
    default: return c.ingredientById.get(content)?.color ?? '#c7ced2';
  }
}

// ------------------------------------------------------------------ 烏龜車小工具
const cap = (ctx: Ctx) => ctx.c.flow.perCart;
export const cartCount = (d: Day, id: string) => { const k = d.carts[id]; return k ? (k.load?.baskets ?? 0) + k.baskets.length : 0; };
const kindsOn = (d: Day, id: string) => new Set(d.carts[id].baskets.map(b => basketKind(d.baskets[b].content)));
const key = (floor: string, pos: [number, number]) => `${floor}@${pos[0]},${pos[1]}`;
const moving = (d: Day, id: string) => d.pushing === id || d.holding?.id === id;
/** v1.1.1 玩家在同一層、離這個位置 8 m 內 */
function playerNear(ctx: Ctx, floor: string, pos: [number, number], r = 8): boolean {
  if (ctx.day.floor !== floor) return false;
  try { const [x, z] = ctx.world.playerPos(); return Math.hypot(x - pos[0], z - pos[1]) < r; } catch { return false; }
}

/** pos 附近（停好的）烏龜車，近的在前 */
export function cartsNear(d: Day, floor: FloorIdT, pos: [number, number], r = 0.9): string[] {
  return Object.entries(d.carts).filter(([id, k]) => k.floor === floor && !moving(d, id) && Math.hypot(k.pos[0] - pos[0], k.pos[1] - pos[1]) <= r)
    .sort((a, b) => Math.hypot(a[1].pos[0] - pos[0], a[1].pos[1] - pos[1]) - Math.hypot(b[1].pos[0] - pos[0], b[1].pos[1] - pos[1])).map(([id]) => id);
}
function newCart(d: Day, floor: FloorIdT, pos: [number, number], extra: Partial<CartState> = {}): string {
  const f = d.flow!; const id = `cart-f${++f.seq}`;
  d.carts[id] = { floor, pos: [pos[0], pos[1]], load: null, baskets: [], ...extra };
  return id;
}
/** 這個位置的那台車（沒有就推一台空車過來） */
function homeCart(d: Day, floor: FloorIdT, pos: [number, number], create = true): string | null {
  return cartsNear(d, floor, pos)[0] ?? (create ? newCart(d, floor, pos) : null);
}
export function addBasket(d: Day, cartId: string, content: string, cooked?: boolean): string {
  const f = d.flow!; const k = d.carts[cartId]; const id = `bk-f${++f.seq}`;
  d.baskets[id] = { floor: k.floor, pos: [k.pos[0], k.pos[1]], content, on: cartId, ...(cooked != null ? { cooked } : {}) };
  k.baskets.push(id);
  return id;
}
function dropBasket(d: Day, bid: string): void {
  for (const k of Object.values(d.carts)) k.baskets = k.baskets.filter(x => x !== bid);
  delete d.baskets[bid];
}
/** 從車上拿掉 n 個某種籃子（從上面拿） */
function takeKind(d: Day, cartId: string, kind: BasketKind, n: number, cooked?: boolean): number {
  const k = d.carts[cartId]; let got = 0;
  for (let i = k.baskets.length - 1; i >= 0 && got < n; i--) {
    const b = d.baskets[k.baskets[i]];
    if (basketKind(b.content) === kind && (cooked == null || !!b.cooked === cooked)) { dropBasket(d, k.baskets[i]); got++; }
  }
  return got;
}
const countKind = (d: Day, ids: string[], kind: BasketKind, cooked?: boolean) =>
  ids.reduce((s, id) => s + d.carts[id].baskets.filter(b => basketKind(d.baskets[b].content) === kind && (cooked == null || !!d.baskets[b].cooked === cooked)).length, 0);

/** 車上的東西（給提示、卡片用）：例「成品 3（漢堡・三明治）」「空籃 4」「髒籃 2」 */
export function cartSummary(ctx: Ctx, id: string): string {
  const d = ctx.day; const k = d.carts[id]; if (!k) return '';
  const n = cartCount(d, id); if (!n) return tr("空車");
  if (k.load) return tr("{0} 籃（{1}）", n, k.load.material);
  const by = new Map<string, number>();
  for (const b of k.baskets) { const c = d.baskets[b].content; const lab = basketKind(c) === 'food' ? basketLabel(ctx.c, c) : basketKind(c) === 'prod' ? basketLabel(ctx.c, c) : basketKind(c) === 'semi' ? tr("半成品") : basketLabel(ctx.c, c); by.set(lab, (by.get(lab) ?? 0) + 1); }
  return [...by].map(([l, x]) => tr("{0}×{1}", l, x)).join(tr("、")) + tr("（{0}/{1}）", n, cap(ctx));
}

// ------------------------------------------------------------------ 誰負責
const role = (ctx: Ctx) => ctx.c.roleById.get(ctx.day.roleId);
const isProd = (ctx: Ctx) => role(ctx)?.dept === 'production';
const isWh = (ctx: Ctx) => role(ctx)?.dept === 'warehouse';
/** 玩家是這一層的生產部 → 同事會等久一點（留給玩家做） */
const mine = (ctx: Ctx, floor: string) => isProd(ctx) && role(ctx)?.startFloor === floor;
const radio = (ctx: Ctx, text: string, floor?: string, kind: 'info' | 'warn' | 'good' | 'bad' = 'info') => {
  ctx.log(`📻 ${text}`, kind);
  if (!floor || floor === ctx.day.floor || mine(ctx, floor)) ctx.ui.toast(`📻 ${text}`);
};

// ------------------------------------------------------------------ 開工
export function initFlow(ctx: Ctx): FlowState {
  const d = ctx.day, F = ctx.c.flow;
  d.flow = { seq: 0, st: {}, shortages: [], washing: [], timers: {}, shipped: 0, by: { sent: 0, shipped: 0, washed: 0, semi: 0, refill: 0 } };
  const active = new Set(d.orders.lines.map(l => l.product));
  for (const s of F.stations) {
    d.flow.st[s.id] = { made: 0, last: d.orders.lines.find(l => l.product === s.product)?.produced ?? 0, stall: null, stallAt: 0, baskets: 0, next: d.clock + (s.every ?? 0) };
    const out = homeCart(d, s.floor, s.out)!;
    if (s.kind === 'product') {
      if (active.has(s.product!) && nextRandom(d) < 0.7) addBasket(d, out, PROD + s.product, true);
      const em = homeCart(d, s.floor, s.empty!)!;
      while (countKind(d, [em], 'empty') < 4) addBasket(d, em, EMPTY, true);
      const di = homeCart(d, s.floor, s.dirty!)!;
      if (!cartCount(d, di) && nextRandom(d) < 0.5) addBasket(d, di, DIRTY, true);
    } else {
      addBasket(d, out, SEMI + pickSemi(ctx, s), true);
    }
  }
  for (const k of F.dirtyCarts) { const id = homeCart(d, k.floor, k.pos)!; if (!cartCount(d, id)) addBasket(d, id, DIRTY, k.cooked); }
  for (const w of F.washers) for (const p of w.dry) { const id = homeCart(d, w.floor, p)!; while (cartCount(d, id) < cap(ctx)) addBasket(d, id, EMPTY, w.cooked); }
  // 生產部：今天會遇到幾次「線邊缺空籃」（自己這層樓的產線）
  if (isProd(ctx)) {
    const floor = role(ctx)?.startFloor;
    let pool = F.stations.filter(s => s.kind === 'product' && active.has(s.product!) && s.floor === floor);
    if (!pool.length) pool = F.stations.filter(s => s.kind === 'product' && active.has(s.product!));
    const span = Math.max(60, d.shiftEnd - d.shiftStart - 120);
    for (let i = 0; i < F.shortagesPerDay && pool.length; i++) {
      const s = pool[Math.floor(nextRandom(d) * pool.length)];
      d.flow.shortages.push({ station: s.id, at: d.shiftStart + 45 + Math.floor((i + nextRandom(d)) / F.shortagesPerDay * span), state: 'pending', since: 0 });
    }
  }
  const lines = d.orders.lines.map(l => { const p = ctx.c.productById.get(l.product)!; return tr("{0} {1} {2}{3}", p.floor, p.name, l.target, p.unit); });
  ctx.log(tr("📋 生管開立今天的工單：{0}。產線照工單開始生產，成品會一籃一籃放到線尾的收料車上。", lines.join(tr("、"))), 'info');
  return d.flow;
}

/** 這一站今天做的半成品（flow.json 的 semis，每天、每幾籃輪替） */
function pickSemi(ctx: Ctx, s: Station): string {
  const list = s.semis ?? [];
  if (!list.length) return '';
  return list[(ctx.day.day * 3 + Math.floor((ctx.day.flow?.st[s.id]?.baskets ?? 0) / 3)) % list.length];
}

/** 產線被線邊卡住了（沒空籃、收料車滿了）→ production.ts 不生產 */
export function flowBlocked(ctx: Ctx, product: string): string | null {
  const f = ctx.day.flow; if (!f) return null;
  const s = ctx.c.flow.stations.find(x => x.product === product); if (!s) return null;
  const st = f.st[s.id];
  return st?.stall === 'empty' ? tr("缺空籃") : st?.stall === 'full' ? tr("收料車滿了") : null;
}

// ------------------------------------------------------------------ 每分鐘
export function tickFlow(ctx: Ctx, minutes: number): void {
  const d = ctx.day; if (!d || minutes <= 0) return;
  const F = ctx.c.flow; const f = d.flow ?? initFlow(ctx);
  const now = d.clock;
  if (now > d.shiftEnd + 180) return;
  // 線邊缺空籃（生產部）：時間到了就開始；玩家補上了就結束
  for (const sh of f.shortages) {
    const s = F.stations.find(x => x.id === sh.station)!;
    if (sh.state === 'pending' && now >= sh.at) {
      sh.state = 'active'; sh.since = now;
      const em = cartsNear(d, s.floor, s.empty!);
      let keep = 1; for (const id of em) { const n = countKind(d, [id], 'empty'); const drop = Math.max(0, n - keep); takeKind(d, id, 'empty', drop); keep = Math.max(0, keep - n); }
      radio(ctx, tr("{0}：洗籃區忙不過來，線邊的空籃快用完了！生產部的人去 {1} 的風乾區推一車空籃過來（烏龜車＋空籃最多 5 個）。", s.name, washerFor(ctx, true).name), s.floor, 'warn');
    } else if (sh.state === 'active') {
      if (countKind(d, cartsNear(d, s.floor, s.empty!), 'empty') >= 2) {
        sh.state = 'done'; f.by.refill++;
        gainXp(ctx, 'flowRescue', tr("補空籃")); gainPoints(ctx, 'flowRescue', tr("補空籃")); d.factory.trust = Math.min(100, d.factory.trust + 2); bump(ctx, 'flowRescue');
        radio(ctx, tr("{0}：空籃補上了，產線繼續跑。謝啦！", s.name), s.floor, 'good');
      } else if (now - sh.since >= F.npc.rescue) {
        sh.state = 'rescued'; fillEmpty(ctx, s);
        radio(ctx, tr("{0}：等太久了，其他同事去推空籃過來（產線停了一陣子）。", s.name), s.floor, 'bad');
      }
    }
  }
  const shortageAt = (s: Station) => f.shortages.some(sh => sh.state === 'active' && F.stations.find(x => x.id === sh.station)!.empty?.join() === s.empty?.join() && F.stations.find(x => x.id === sh.station)!.floor === s.floor);

  for (const s of F.stations) {
    const st = f.st[s.id] ??= { made: 0, last: 0, stall: null, stallAt: 0, baskets: 0, next: now };
    if (s.kind === 'product') {
      const l = d.orders.lines.find(x => x.product === s.product);
      if (!l) continue;
      st.made += Math.max(0, l.produced - st.last); st.last = l.produced;
      const p = ctx.c.productById.get(s.product!)!;
      const units = p.ratePerMinute * F.minutesPerBasket;
      let guard = 0;
      while (st.made >= units && guard++ < 6) {
        const out = homeCart(d, s.floor, s.out)!;
        if (cartCount(d, out) >= cap(ctx)) { setStall(ctx, s, st, 'full'); break; }
        const em = cartsNear(d, s.floor, s.empty!).find(id => countKind(d, [id], 'empty', true) > 0);
        if (!em) { setStall(ctx, s, st, 'empty'); break; }
        takeKind(d, em, 'empty', 1, true);
        addBasket(d, out, PROD + s.product, true);
        st.made -= units; st.baskets++; st.stall = null; st.warned = false;
        if (st.baskets % F.dirtyEvery === 0) { const di = homeCart(d, s.floor, s.dirty!)!; if (cartCount(d, di) < cap(ctx)) addBasket(d, di, DIRTY, true); }
      }
      if (st.made < units && st.stall) st.stall = null;
      // 同事：空籃剩 1 個就補（缺空籃事件中不補）
      const emCount = countKind(d, cartsNear(d, s.floor, s.empty!), 'empty');
      const tk = 'empty:' + key(s.floor, s.empty!);
      if (emCount <= 1 && !shortageAt(s)) { f.timers[tk] ??= now; if (now - f.timers[tk] >= F.npc.refill) { fillEmpty(ctx, s); delete f.timers[tk]; } } else delete f.timers[tk];
      // 同事：收料車滿了就推去成品電梯（玩家是這層的生產部 → 等久一點）
      const out = cartsNear(d, s.floor, s.out)[0];
      const ok = 'out:' + key(s.floor, s.out);
      if (out && cartCount(d, out) >= cap(ctx)) { f.timers[ok] ??= now; if (now - f.timers[ok] >= F.npc.send * (mine(ctx, s.floor) ? 2 : 1)) { sendToShipping(ctx, out, s, false); delete f.timers[ok]; } } else delete f.timers[ok];
    } else {
      // 半成品：每 every 分鐘做好一籃（上班時間內）
      if (now >= st.next && now <= d.shiftEnd) {
        const out = homeCart(d, s.floor, s.out)!;
        if (cartCount(d, out) < cap(ctx)) { addBasket(d, out, SEMI + pickSemi(ctx, s), true); st.baskets++; st.next = now + (s.every ?? 15); st.stall = null; }
        else { st.stall = 'full'; st.next = now + 2; }
      }
      const out = cartsNear(d, s.floor, s.out)[0];
      const ok = 'semi:' + key(s.floor, s.out);
      if (out && cartCount(d, out) >= cap(ctx)) { f.timers[ok] ??= now; if (now - f.timers[ok] >= F.npc.semi * (mine(ctx, s.floor) ? 2 : 1)) {
        const n = takeKind(d, out, 'semi', cap(ctx)); delete f.timers[ok]; st.stall = null;
        ctx.log(tr("（同事把 {0} 籃{1}推去{2}）", n, s.name, s.toName));
      } } else delete f.timers[ok];
    }
  }
  // 同事：髒籃車滿了就推去洗
  const dirtySpots = [...F.stations.filter(s => s.dirty).map(s => ({ floor: s.floor, pos: s.dirty!, cooked: true })), ...F.dirtyCarts];
  const seen = new Set<string>();
  for (const sp of dirtySpots) {
    const k = key(sp.floor, sp.pos); if (seen.has(k)) continue; seen.add(k);
    const id = cartsNear(d, sp.floor, sp.pos)[0];
    const tk = 'dirty:' + k;
    if (id && countKind(d, [id], 'dirty') >= cap(ctx)) { f.timers[tk] ??= now; if (now - f.timers[tk] >= F.npc.collect * (mine(ctx, sp.floor) ? 2 : 1)) {
      const n = takeKind(d, id, 'dirty', cap(ctx)); delete f.timers[tk];
      f.washing.push({ washer: washerFor(ctx, sp.cooked).id, n, ready: now + F.washMinutes });
      ctx.log(tr("（同事把 {0} 個髒籃推去{1}）", n, washerFor(ctx, sp.cooked).name));
    } } else delete f.timers[tk];
  }
  // 洗好的籃子 → 風乾區
  for (const w of f.washing.filter(x => x.ready <= now)) {
    const W = F.washers.find(x => x.id === w.washer)!;
    let n = w.n;
    for (const p of W.dry) { const id = homeCart(d, W.floor, p)!; while (n > 0 && cartCount(d, id) < cap(ctx) && (!cartCount(d, id) || kindsOn(d, id).has('empty'))) { addBasket(d, id, EMPTY, W.cooked); n--; } }
  }
  f.washing = f.washing.filter(x => x.ready > now);
  // 1F：成品電梯到了 → 倉儲拉出電梯（arrived）→ 出貨區排隊（queued）→ 裝車出貨
  for (const [id, k] of Object.entries(d.carts)) {
    if (k.tag !== 'ship' || moving(d, id)) continue;
    // v1.1.1 玩家就在旁邊時，出貨組先不動這台車（以前會在玩家眼前瞬間消失、又在別處冒出來）
    if (playerNear(ctx, k.floor, k.pos)) continue;
    const t = now - (k.since ?? now);
    if (k.stage === 'arrived' && t >= F.npc.pull * (isWh(ctx) ? 3 : 1)) toQueue(ctx, id, false);
    else if (k.stage === 'queued' && t >= F.npc.load) loadTruck(ctx, id, false);
  }
}

function setStall(ctx: Ctx, s: Station, st: FlowState['st'][string], why: 'empty' | 'full'): void {
  if (st.stall !== why) { st.stall = why; st.stallAt = ctx.day.clock; }
  if (st.warned) return;
  st.warned = true;
  if (why === 'empty') radio(ctx, tr("{0}：沒有空籃了，成品裝不下，產線先停！", s.name), s.floor, 'warn');
  else if (mine(ctx, s.floor)) radio(ctx, tr("{0}：成品收料車滿了（5 籃），快推去{1}！", s.name, s.toName), s.floor, 'warn');
}

function fillEmpty(ctx: Ctx, s: Station): void {
  const d = ctx.day; const id = homeCart(d, s.floor, s.empty!)!;
  if (cartCount(d, id) && !kindsOn(d, id).has('empty')) return;
  while (cartCount(d, id) < cap(ctx)) addBasket(d, id, EMPTY, true);
}
export const washerFor = (ctx: Ctx, cooked: boolean) => ctx.c.flow.washers.find(w => w.cooked === cooked) ?? ctx.c.flow.washers[0];

// ------------------------------------------------------------------ 成品：電梯 → 1F 收貨 → 出貨
function freeSpot(ctx: Ctx, spots: [number, number][]): [number, number] | null {
  return spots.find(p => !cartsNear(ctx.day, '1F', p, 0.8).length) ?? null;
}
/** 推進成品電梯送到 1F（byPlayer：玩家自己用無線電通知） */
function sendToShipping(ctx: Ctx, cartId: string, s: Station | null, byPlayer: boolean): void {
  const d = ctx.day, F = ctx.c.flow; const k = d.carts[cartId];
  let spot = freeSpot(ctx, F.shipping.arrive);
  if (!spot) { // 電梯口塞住了：最早到的那台先拉去出貨區
    const oldest = Object.entries(d.carts).filter(([, x]) => x.tag === 'ship' && x.stage === 'arrived').sort((a, b) => (a[1].since ?? 0) - (b[1].since ?? 0))[0];
    if (oldest) toQueue(ctx, oldest[0], false);
    spot = freeSpot(ctx, F.shipping.arrive) ?? F.shipping.arrive[0];
  }
  const n = cartCount(d, cartId);
  const from = s?.name ?? tr("{0} 成品", k.floor);
  k.floor = '1F'; k.pos = [spot[0], spot[1]]; k.tag = 'ship'; k.stage = 'arrived'; k.since = d.clock; k.from = from;
  for (const b of k.baskets) { d.baskets[b].floor = '1F'; d.baskets[b].pos = [spot[0], spot[1]]; }
  if (d.pushing === cartId) d.pushing = null;
  if (byPlayer) {
    d.flow!.by.sent++; bump(ctx, 'flowSent'); gainXp(ctx, 'flow', tr("成品送電梯"));
    ctx.log(tr("📻 你：「{0}成品 {1} 籃下去 1F 囉，出貨倉儲課請收貨！」倉儲：「收到，馬上拉出來。」", from, n), 'good');
  } else radio(ctx, tr("{0}：成品 {1} 籃進成品電梯了，出貨倉儲課請收貨。", from, n), isWh(ctx) ? '1F' : undefined);
}
function toQueue(ctx: Ctx, cartId: string, byPlayer: boolean): void {
  const d = ctx.day, F = ctx.c.flow; const k = d.carts[cartId];
  const spot = freeSpot(ctx, F.shipping.queue);
  if (!spot) { const oldest = Object.entries(d.carts).filter(([, x]) => x.tag === 'ship' && x.stage === 'queued').sort((a, b) => (a[1].since ?? 0) - (b[1].since ?? 0))[0]; if (oldest) loadTruck(ctx, oldest[0], false); }
  const p = freeSpot(ctx, F.shipping.queue) ?? F.shipping.queue[0];
  k.pos = [p[0], p[1]]; k.stage = 'queued'; k.since = d.clock;
  for (const b of k.baskets) d.baskets[b].pos = [p[0], p[1]];
  if (d.pushing === cartId) d.pushing = null;
  if (byPlayer) { gainXp(ctx, 'flow', tr("收貨")); ctx.log(tr("✍️ 收貨簽收：{0} {1} 籃，拉出電梯送到出貨區。", k.from ?? '', cartCount(d, cartId)), 'good'); }
}
function loadTruck(ctx: Ctx, cartId: string, byPlayer: boolean): void {
  const d = ctx.day; const n = cartCount(d, cartId); const from = d.carts[cartId].from ?? '';
  for (const b of [...d.carts[cartId].baskets]) dropBasket(d, b);
  delete d.carts[cartId];
  if (d.pushing === cartId) d.pushing = null;
  d.flow!.shipped += n;
  if (byPlayer) { d.flow!.by.shipped += n; gainXp(ctx, 'flow', tr("出貨")); ctx.log(tr("🚚 你把 {0} 的 {1} 籃成品裝上出貨車。", from, n), 'good'); }
  else ctx.log(tr("🚚 出貨組把 {0} 籃成品裝上冷藏車（今天已出貨 {1} 籃）。", n, d.flow!.shipped));
}

// ------------------------------------------------------------------ 玩家的動作
const done = (ctx: Ctx) => { bus.emit('sfx', 'click'); ctx.world.refreshActors(); ctx.save(); };

/** 烏龜車卡片多出來的選項：兩台車之間搬籃子、1F 收貨簽收、出貨 */
export function cartChoices(ctx: Ctx, id: string): CardChoice[] {
  const d = ctx.day; const k = d.carts[id]; const out: CardChoice[] = [];
  if (k.tag === 'ship' && k.stage === 'arrived') out.push({ text: tr("✍️ 收貨簽收，拉出電梯送到出貨區"), tone: 'good', fn: () => { ctx.ui.close(); toQueue(ctx, id, true); done(ctx); } });
  if (k.tag === 'ship' && k.stage === 'queued') out.push({ text: tr("🚚 裝上出貨車"), tone: 'good', fn: () => { ctx.ui.close(); loadTruck(ctx, id, true); done(ctx); } });
  const P = d.pushing;
  if (P && P !== id && !d.carts[P].load && !k.load) {
    if (k.baskets.length) out.push({ text: tr("⬅️ 把這台的籃子收到我推的車上（最多 {0} 個）", cap(ctx)), tone: 'primary', fn: () => { ctx.ui.close(); moveBetween(ctx, id, P); } });
    if (d.carts[P].baskets.length) out.push({ text: tr("➡️ 把我車上的籃子放到這台"), fn: () => { ctx.ui.close(); moveBetween(ctx, P, id); } });
  }
  return out;
}
/** 從 a 車上面拿籃子疊到 b 車：同一種才可以疊（髒籃不能和乾淨的籃子、食材放同一台） */
export function moveBetween(ctx: Ctx, from: string, to: string): number {
  const d = ctx.day; const A = d.carts[from], B = d.carts[to];
  let n = 0;
  while (A.baskets.length && cartCount(d, to) < cap(ctx)) {
    const bid = A.baskets[A.baskets.length - 1]; const kind = basketKind(d.baskets[bid].content);
    const onB = kindsOn(d, to);
    const clash = onB.size && ((kind === 'dirty') !== onB.has('dirty') || (onB.has('dirty') && onB.size > 1));
    if (clash) { if (!n) ctx.ui.toast(tr("🙅 髒籃不能和乾淨的空籃、食材、成品放在同一台車上（會交叉污染）。")); break; }
    A.baskets.pop(); B.baskets.push(bid); d.baskets[bid].on = to; d.baskets[bid].floor = B.floor; d.baskets[bid].pos = [B.pos[0], B.pos[1]];
    n++;
  }
  if (n) ctx.ui.toast(tr("🧺 搬了 {0} 籃（這台現在 {1}/{2}）。", n, cartCount(d, to), cap(ctx)));
  else if (cartCount(d, to) >= cap(ctx)) ctx.ui.toast(tr("🚫 一台烏龜車最多疊 {0} 籃。", cap(ctx)));
  done(ctx);
  return n;
}

/** 電梯卡片：推著成品車在成品電梯前 → 送 1F＋無線電 */
export function freightChoices(ctx: Ctx, eqId: string): CardChoice[] {
  const d = ctx.day; const P = d.pushing; if (!P) return [];
  const k = d.carts[P];
  if (!k.baskets.some(b => basketKind(d.baskets[b].content) === 'prod')) return [];
  const s = ctx.c.flow.stations.find(x => x.kind === 'product' && x.to.includes(eqId)) ?? null;
  if (!s && eqId !== ctx.c.flow.shipping.lift) return [];
  return [{ text: tr("📻 送到 1F 出貨，用無線電通知出貨倉儲課收貨"), tone: 'primary', fn: () => {
    ctx.ui.close();
    const n = cartCount(d, P);
    sendToShipping(ctx, P, s, true); done(ctx);
    ctx.ui.card({ name: tr("📻 無線電"), role: tr("出貨倉儲課"), color: '#3f78a8',
      text: tr("你：「這裡是{0}，成品 {1} 籃進成品電梯下去 1F 了，麻煩收貨！」\n倉儲：「收到！我們去電梯口拉出來，直接排出貨。」", s?.name ?? k.floor, n),
      sections: [{ label: tr("流程"), items: [tr("成品裝籃 → 烏龜車（最多 5 籃）→ 成品電梯 → 無線電通知 → 1F 倉儲收貨簽收、拉出電梯 → 出貨區裝冷藏車")], tone: 'info' }],
      choices: [{ text: tr("好"), tone: 'primary', fn: () => ctx.ui.close() }] });
  } }];
}

/** 設備互動：洗籃區入口、風乾區、半成品的下一站、出貨區。回傳 true＝處理掉了 */
export function flowUse(ctx: Ctx, eqId: string): boolean {
  const d = ctx.day, F = ctx.c.flow; const e = ctx.world.rt().equipment.get(eqId)?.data; if (!e) return false;
  const it = e.interaction;
  const P = d.pushing;
  if (it?.type === 'basketInlet') { washInlet(ctx, it.washer); return true; }
  if (it?.type === 'dryArea') { dryArea(ctx, it.washer); return true; }
  // 半成品送下一站
  const semiSt = F.stations.find(s => s.kind === 'semi' && s.to.includes(eqId));
  if (semiSt && P && d.carts[P].baskets.some(b => basketKind(d.baskets[b].content) === 'semi')) {
    const n = takeKind(d, P, 'semi', 99);
    d.flow!.by.semi += n; gainXp(ctx, 'flow', tr("半成品送下一站"));
    ctx.log(tr("🧺 把 {0} 籃半成品交給{1}：冷卻後送熟切、包裝。", n, semiSt.toName), 'good');
    ctx.ui.toast(tr("🧺 半成品 {0} 籃交給下一站（{1}）。空車可以推回去。", n, semiSt.toName)); done(ctx);
    return true;
  }
  // 出貨區
  if (eqId === F.shipping.dock && P && d.carts[P].baskets.some(b => basketKind(d.baskets[b].content) === 'prod')) {
    d.carts[P].from ??= tr("成品"); loadTruck(ctx, P, true); done(ctx); return true;
  }
  return false;
}

function washInlet(ctx: Ctx, washerId: string): void {
  const d = ctx.day, F = ctx.c.flow; const W = F.washers.find(w => w.id === washerId)!;
  const P = d.pushing;
  const dirtyOn = P ? d.carts[P].baskets.filter(b => basketKind(d.baskets[b].content) === 'dirty') : [];
  if (dirtyOn.length) {
    const wrong = dirtyOn.filter(b => !!d.baskets[b].cooked !== W.cooked);
    if (wrong.length) {
      const other = washerFor(ctx, !W.cooked);
      ctx.ui.card({ name: W.name, role: tr("洗籃區入口"), color: '#8a7a5a', text: tr("這裡只洗{0}。你車上有 {1} 個{2}，要送到「{3}」。生熟用籃分開洗，才不會交叉污染。", W.cooked ? tr("熟食用籃") : tr("生食・一般用籃"), wrong.length, W.cooked ? tr("生食・一般用籃") : tr("熟食用籃"), other.name),
        choices: [{ text: tr("知道了"), tone: 'primary', fn: () => ctx.ui.close() }] });
      return;
    }
    const n = dirtyOn.length;
    for (const b of dirtyOn) dropBasket(d, b);
    d.flow!.washing.push({ washer: W.id, n, ready: d.clock + F.washMinutes });
    d.flow!.by.washed += n; bump(ctx, 'basketsWashed', n); gainXp(ctx, 'flow', tr("髒籃送洗"));
    ctx.log(tr("🧺 把 {0} 個髒籃交給洗籃人員（{1}）。約 {2} 分鐘後洗好、推到風乾區。", n, W.name, F.washMinutes), 'good');
    ctx.ui.toast(tr("🧺 髒籃 {0} 個交給洗籃人員了。空的烏龜車可以推回去、或裝風乾區的空籃。", n)); done(ctx);
    return;
  }
  const q = d.flow!.washing.filter(w => w.washer === W.id);
  ctx.ui.card({ name: W.name, role: tr("洗籃區入口"), color: '#8a7a5a',
    text: tr("髒籃連同烏龜車推過來，交給洗籃人員。洗好的籃子會推到旁邊的風乾區。\n這裡洗：{0}。", W.cooked ? tr("熟食用籃（包裝線、調理區用的）") : tr("生食・一般用籃（前處理、倉儲用的）")),
    sections: [{ label: tr("現在"), items: [q.length ? tr("洗籃機裡有 {0} 個，{1} 洗好", q.reduce((s, x) => s + x.n, 0), fmtClock(Math.min(...q.map(x => x.ready)))) : tr("洗籃機現在是空的")], tone: 'info' }],
    choices: [{ text: tr("好"), tone: 'primary', fn: () => ctx.ui.close() }] });
}

function dryArea(ctx: Ctx, washerId: string): void {
  const d = ctx.day, F = ctx.c.flow; const W = F.washers.find(w => w.id === washerId)!;
  const carts = W.dry.flatMap(p => cartsNear(d, W.floor, p));
  const n = countKind(d, carts, 'empty');
  const P = d.pushing;
  const choices: CardChoice[] = [];
  if (P && n && !kindsOn(d, P).has('dirty') && cartCount(d, P) < cap(ctx)) choices.push({ text: tr("🧺 把空籃裝到我推的車上（最多 {0} 個）", cap(ctx)), tone: 'primary', fn: () => {
    ctx.ui.close(); let moved = 0;
    for (const id of carts) { while (cartCount(d, P) < cap(ctx) && countKind(d, [id], 'empty')) { const bid = [...d.carts[id].baskets].reverse().find(b => d.baskets[b].content === EMPTY)!;
      d.carts[id].baskets = d.carts[id].baskets.filter(x => x !== bid); d.carts[P].baskets.push(bid); d.baskets[bid].on = P; d.baskets[bid].floor = d.carts[P].floor; moved++; } }
    ctx.ui.toast(tr("🧺 裝了 {0} 個空籃（{1}）。推去缺籃的線邊，靠近那台空籃車按 E 放過去。", moved, W.cooked ? tr("熟食用") : tr("生食・一般用"))); done(ctx);
  } });
  ctx.ui.card({ name: tr("風乾區"), role: W.name, color: '#6fa8c9',
    text: tr("洗好的籃子推到這裡風乾。線邊缺籃時：推一台烏龜車過來裝空籃（最多 5 個），或直接把整台推走。\n現在有 {0} 個空籃（{1}）。", n, W.cooked ? tr("熟食用") : tr("生食・一般用")),
    choices: [...choices, { text: tr("好"), fn: () => ctx.ui.close() }] });
}

/** 送到了：任務投料用掉的籃子變成髒籃，放到這層樓最近的髒籃車（滿了就由同事先收走） */
export function toDirty(ctx: Ctx, bid: string): void {
  const d = ctx.day, F = ctx.c.flow; const b = d.baskets[bid]; if (!b) return;
  if (!d.flow) initFlow(ctx);
  const cooked = d.floor === '2F' || d.floor === '3F';
  const spots = [...F.stations.filter(s => s.dirty && s.floor === d.floor).map(s => s.dirty!), ...F.dirtyCarts.filter(x => x.floor === d.floor).map(x => x.pos)];
  const [x, z] = ctx.world.playerPos();
  spots.sort((a, c) => Math.hypot(a[0] - x, a[1] - z) - Math.hypot(c[0] - x, c[1] - z));
  for (const k of Object.values(d.carts)) k.baskets = k.baskets.filter(q => q !== bid);
  if (d.holding?.id === bid) d.holding = null;
  const target = spots.map(p => homeCart(d, d.floor, p)!).find(id => cartCount(d, id) < cap(ctx) && (!cartCount(d, id) || kindsOn(d, id).has('dirty')));
  if (!target) { delete d.baskets[bid]; return; }
  Object.assign(b, { content: DIRTY, cooked, used: false, on: target, floor: d.floor, pos: [...d.carts[target].pos] as [number, number] });
  d.carts[target].baskets.push(bid);
}

// ------------------------------------------------------------------ 任務欄「📻 線邊狀況」、訂單看板
export function flowTodos(ctx: Ctx): string[] {
  const d = ctx.day, F = ctx.c.flow, f = d.flow; if (!f) return [];
  const out: string[] = [];
  for (const sh of f.shortages.filter(x => x.state === 'active')) {
    const s = F.stations.find(x => x.id === sh.station)!;
    out.push(tr("🆘 {0} 缺空籃 → 到 {1} 的風乾區推一車空籃過去（{2} 前）", s.name, washerFor(ctx, true).name, fmtClock(sh.since + F.npc.rescue)));
  }
  if (isProd(ctx)) {
    const floor = role(ctx)?.startFloor;
    for (const s of F.stations.filter(x => x.floor === floor)) {
      const o = cartsNear(d, s.floor, s.out)[0];
      if (o && cartCount(d, o) >= cap(ctx)) out.push(s.kind === 'product' ? tr("📦 {0}：成品收料車滿了 → 推去{1}、無線電通知倉儲", s.name, s.toName) : tr("🧺 {0}：半成品滿了 → 推去{1}", s.name, s.toName));
    }
    const seen = new Set<string>();
    for (const s of F.stations.filter(x => x.floor === floor && x.dirty)) {
      const k = key(s.floor, s.dirty!); if (seen.has(k)) continue; seen.add(k);
      const id = cartsNear(d, s.floor, s.dirty!)[0];
      if (id && countKind(d, [id], 'dirty') >= cap(ctx)) out.push(tr("🧺 {0} 的髒籃車滿了 → 推去 {1} 入口", s.name, washerFor(ctx, true).name));
    }
  }
  if (isWh(ctx)) {
    const n = Object.values(d.carts).filter(k => k.tag === 'ship' && k.stage === 'arrived').length;
    if (n) out.push(tr("📦 1F 成品電梯口有 {0} 車成品 → 收貨簽收、拉出電梯送出貨區", n));
  }
  return out.slice(0, 4);
}
/** 訂單看板：每條線的線邊狀況 */
export function lineStatus(ctx: Ctx, product: string): string {
  const d = ctx.day, F = ctx.c.flow; const s = F.stations.find(x => x.product === product); if (!s || !d.flow) return '';
  const st = d.flow.st[s.id];
  const outN = cartsNear(d, s.floor, s.out).reduce((n, id) => n + cartCount(d, id), 0);
  const em = countKind(d, cartsNear(d, s.floor, s.empty!), 'empty');
  const di = countKind(d, cartsNear(d, s.floor, s.dirty!), 'dirty');
  return tr("線邊：成品 {0}/{1} 籃・空籃 {2}・髒籃 {3}・今天裝了 {4} 籃{5}", outN, cap(ctx), em, di, st?.baskets ?? 0, st?.stall === 'empty' ? tr("・⚠ 缺空籃") : st?.stall === 'full' ? tr("・⚠ 收料車滿了") : '');
}
